import { useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download } from "../components/analytics/analytics-types";
import { AppPageParams } from "../components/apps-components/apps-types";
import Layout from "../components/layout/Layout";
import AppIntro from "../components/apps-components/AppIntro";
import VersionSection from "../components/apps-components/VersionSection";
import LinkDetailsTable from "../components/apps-components/LinkDetailsTable";
import GlobalLineChart from "../components/analytics/LineChart";
import LinkChart from "../components/analytics/LinkChart";
import Footer from "../components/layout/Footer";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { Link as RouterLink } from "react-router-dom";
import reportWebVitals from "../reportWebVitals";
import {
  useLinksOneApp,
  useAppFiltered,
  useApp,
  useDownloadApp,
  useDownloadLink,
} from "./api/api";
import checkPlatform from "../utils/checkPlatform";

/* Application Details Page --> A responsive <Grid /> divided in different sections: 
  <AppIntro />, <VersionSection />, <LinkSection /> (in ../components/apps) and 
  2 analytical components (in ../components/analytics) */
export default function ApplicationDetailsPage() {
  const { appID } = useParams<keyof AppPageParams>() as AppPageParams;

  const { t } = useTranslation();

  /* State variables to keep app infrmation */
  const appColumns = [t("Version"), t("Release Date"), t("Changelog")];
  const linkColumns = [t("Versions"), t("Validity end"), t("Progress")];

  /* State variables to keep analytical data */
  const [openLinkGeneration, setOpenLinkGeneration] = useState(false);
  const [openGeneratedLinkSummary, setOpenGeneratedLinkSummary] =
    useState(false);

  const { appSearched, versions, isLoadingAppSearched, isErrorAppSearched } =
    useAppFiltered(appID);
  const { linksForOneApp, isLoadingLinksOneApp, isErrorLinksOneApp } =
    useLinksOneApp();
  const { app, isLoadingApp, isErrorApp } = useApp(appSearched.id);

  const { downloadsPerApp, isLoadingDownloadsPerApp, isErrorDownloadsPerApp } =
    useDownloadApp(appSearched.id);

  const {
    downloadsPerLink,
    isLoadingDownloadsPerLink,
    isErrorDownloadsPerLink,
  } = useDownloadLink(appSearched.id);

  const download4Plot = downloadsPerApp
    .flat()
    .map((item) => item.downloads)
    .flat();
  let vectornew: Download[] = [];
  vectornew.map((item) =>
    item.timestamp.replace(item.timestamp, item.timestamp.slice(0, 10))
  );

  return (
    <div>
      <Layout />

      <Container sx={{ mt: 3, mb: 2 }}>
        <Box mb={3}>
          <Button
            to="/applications"
            variant="outlined"
            startIcon={<ArrowBackIosIcon />}
            component={RouterLink}
          >
            {t("All applications")}
          </Button>
        </Box>

        <AppIntro
          appID={app.id}
          description={app.description}
          platform={checkPlatform(versions)}
          appName={appID}
          versions={versions}
          openLinkGeneration={openLinkGeneration}
          setOpenLinkGeneration={setOpenLinkGeneration}
        />

        <Divider sx={{ mt: 2 }} />

        <VersionSection
          name={appID}
          versions={versions}
          appID={app.id}
          columns={appColumns}
          platform={checkPlatform(versions)}
          isLoading={isLoadingApp}
          open={openLinkGeneration}
          setOpen={setOpenLinkGeneration}
        />

        <Divider />

        <Stack spacing={2} paddingTop={1}>
          <Typography variant="h5">{t("Link")}</Typography>
          <LinkDetailsTable
            name={appID}
            columns={linkColumns}
            links={linksForOneApp.filter((element) => element.appId === app.id)}
            isLoading={isLoadingLinksOneApp}
            openDialog={openGeneratedLinkSummary}
            setOpenDialog={setOpenGeneratedLinkSummary}
          />
        </Stack>

        <Divider />

        <Stack marginTop={2} spacing={2}>
          <Typography variant="h5">{t("Statistics")}</Typography>
          <Grid container spacing={1} sx={{ pr: 1 }}>
            <Grid item md={6} xs={12}>
              <GlobalLineChart
                title={t("Application total downloads")}
                downloads={download4Plot}
                loading={isLoadingDownloadsPerApp}
                isError={isErrorDownloadsPerApp}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <LinkChart
                downloads={downloadsPerLink}
                loading={isLoadingDownloadsPerLink}
                isDashboard={false}
                isError={isErrorDownloadsPerLink}
              />
            </Grid>
          </Grid>
        </Stack>
      </Container>
      <div
        style={{
          position: "relative",
          left: 0,
          bottom: 0,
          right: 0,
        }}
      >
        <Footer />
      </div>
    </div>
  );
}

reportWebVitals();
